import React, { useRef, useEffect } from 'react';

interface TableContextMenuProps {
  editor: any;
  position: { x: number; y: number } | null;
  onClose: () => void;
}

const TableContextMenu: React.FC<TableContextMenuProps> = ({ editor, position, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Click outside and escape key handling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (position) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [position, onClose]);

  // Fixed positioning for multiple tables
  useEffect(() => {
    if (position && menuRef.current) {
      const menu = menuRef.current;
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      };
      
      // Get menu dimensions
      menu.style.visibility = 'hidden';
      menu.style.display = 'block';
      const menuRect = menu.getBoundingClientRect();
      menu.style.visibility = 'visible';
      
      // Calculate optimal position
      let adjustedX = position.x;
      let adjustedY = position.y;
      
      // Prevent horizontal overflow
      if (position.x + menuRect.width > viewport.width) {
        adjustedX = position.x - menuRect.width;
      }
      
      // Prevent vertical overflow
      if (position.y + menuRect.height > viewport.height) {
        adjustedY = position.y - menuRect.height;
      }
      
      // Ensure menu stays within viewport
      adjustedX = Math.min(adjustedX, viewport.width - menuRect.width);
      adjustedY = Math.min(adjustedY, viewport.height - menuRect.height);
      
      // Apply positioning
      menu.style.position = 'fixed';
      menu.style.left = `${adjustedX}px`;
      menu.style.top = `${adjustedY}px`;
    }
  }, [position]);

  if (!position || !editor) return null;

  const menuItems = [
    {
      label: 'Add column before',
      icon: 'ti-column-insert-left',
      action: () => {
        editor.chain().focus().addColumnBefore().run();
        onClose();
      }
    },
    {
      label: 'Add column after',
      icon: 'ti-column-insert-right',
      action: () => {
        editor.chain().focus().addColumnAfter().run();
        onClose();
      }
    },
    {
      label: 'Delete column',
      icon: 'ti-column-remove',
      action: () => {
        editor.chain().focus().deleteColumn().run();
        onClose();
      },
      danger: true
    },
    { divider: true },
    {
      label: 'Add row before',
      icon: 'ti-row-insert-top',
      action: () => {
        editor.chain().focus().addRowBefore().run();
        onClose();
      }
    },
    {
      label: 'Add row after',
      icon: 'ti-row-insert-bottom',
      action: () => {
        editor.chain().focus().addRowAfter().run();
        onClose();
      }
    },
    {
      label: 'Delete row',
      icon: 'ti-row-remove',
      action: () => {
        editor.chain().focus().deleteRow().run();
        onClose();
      },
      danger: true
    },
    { divider: true },
    {
      label: 'Merge cells',
      icon: 'ti-arrows-join',
      action: () => {
        editor.chain().focus().mergeCells().run();
        onClose();
      }
    },
    {
      label: 'Split cell',
      icon: 'ti-arrows-split',
      action: () => {
        editor.chain().focus().splitCell().run();
        onClose();
      }
    },
    { divider: true },
    {
      label: 'Delete table',
      icon: 'ti-table-off',
      action: () => {
        editor.chain().focus().deleteTable().run();
        onClose();
      },
      danger: true
    }
  ];

  return (
    <div
      ref={menuRef}
      className="dropdown-menu show bg-white border rounded shadow-lg py-1"
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 9999,
        minWidth: '200px',
        maxHeight: '400px',
        overflowY: 'auto'
      }}
    >
      {menuItems.map((item, index) => {
        if (item.divider) {
          return <div key={index} className="dropdown-divider" />;
        }

        return (
          <button
            key={index}
            className={`dropdown-item d-flex align-items-center px-3 py-2 border-0 bg-transparent w-100 text-start ${
              item.danger ? 'text-danger' : 'text-dark'
            }`}
            onClick={item.action}
          >
            <i className={`ti ${item.icon} me-3 fs-16`}></i>
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default TableContextMenu;
